'use client';

import { useEffect, useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { HowItWorksAnimation } from "@/components/marketing/HowItWorksAnimation";

function TokenChart() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });
  const [animated, setAnimated] = useState(false);
  useEffect(() => { if (inView) setAnimated(true); }, [inView]);

  return (
    <div ref={ref} className="glass-panel rounded-2xl p-8 max-w-xl">
      <div className="flex justify-between items-start mb-6">
        <div>
          <p className="font-bold text-foreground">Token Consumption</p>
          <p className="text-sm text-muted-foreground">Average tokens per page fetch</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-display font-bold text-primary">~80%</p>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Reduction</p>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <div className="flex justify-between text-sm mb-1.5">
            <span className="flex items-center gap-2 text-muted-foreground font-medium">
              <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
              Raw HTML Fetch
            </span>
            <span className="font-mono text-foreground">15,420 tokens</span>
          </div>
          <div className="h-5 w-full bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-red-400 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: animated ? "100%" : "0%" }}
              transition={{ duration: 1.4, ease: "easeOut", delay: 0.1 }}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-sm mb-1.5">
            <span className="flex items-center gap-2 font-medium text-foreground">
              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
              With Legible
            </span>
            <span className="font-mono text-primary font-bold">2,840 tokens</span>
          </div>
          <div className="h-5 w-full bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-blue-500 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: animated ? "19%" : "0%" }}
              transition={{ duration: 1.4, ease: "easeOut", delay: 0.4 }}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-100 flex items-start gap-3">
        <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600"><path d="M20 6 9 17l-5-5" /></svg>
        </div>
        <p className="text-sm text-foreground leading-relaxed">
          <strong>Save context window space.</strong> Stop wasting AI's reading budget on nested divs, inline styles, and scripts. Legible delivers pure semantic Markdown that AI models parse instantly.
        </p>
      </div>
    </div>
  );
}

const steps = [
  {
    number: "01",
    title: "Connect your existing site",
    body: "Install Legible on your CMS or use the hosted setup path. We read your content without forcing a migration or a redesign.",
  },
  {
    number: "02",
    title: "Choose what should represent your brand",
    body: "Select the content you want AI systems and chat experiences to use: blog posts, FAQs, docs, products, case studies, or uploaded knowledge.",
  },
  {
    number: "03",
    title: "Legible handles the technical layer for you",
    body: "Legible generates and maintains the AI visibility stack automatically, from discovery files and Markdown delivery to permissions and structured metadata. Technical teams still get a coherent implementation, but marketing teams do not have to manage the plumbing.",
  },
  {
    number: "04",
    title: "Measure, improve, and deploy answers",
    body: "Use Content Chat and analytics to see what AI systems can understand, find content gaps, and prove the value internally. Then deploy the same content layer into Intercom, Zendesk, or your own chatbot experience.",
  },
];

export default function HowItWorksClient() {
  return (
    <>
      {/* HERO — animation component */}
      <HowItWorksAnimation />

      {/* THE PROBLEM */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-primary uppercase mb-4">The problem</p>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-5">
            <span className="text-primary">#</span> AI answer engines can&apos;t cite what they can&apos;t read
          </h2>
          <p className="text-lg text-muted-foreground mb-12 max-w-2xl">
            Your team may already have strong content. The problem is that AI answer engines do not consume it the same way a human does. If they waste their budget on layout noise, your content becomes harder to quote, trust, and reuse in answers.
          </p>
          <TokenChart />
        </div>
      </section>

      {/* THE SOLUTION — STEPS */}
      <section className="py-24 bg-gray-50 border-y border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-primary uppercase mb-4">Setup</p>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
            <span className="text-primary">#</span> One connection. Everything else is automatic.
          </h2>
          <p className="text-xl text-muted-foreground mb-16">
            The goal is simple: better AI visibility and better AI answers, without creating a new operational burden for your team.
          </p>

          <div className="space-y-10">
            {steps.map((step) => (
              <div key={step.number} className="flex gap-8 items-start">
                <div className="shrink-0 w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <span className="font-display font-extrabold text-primary text-lg">{step.number}</span>
                </div>
                <div>
                  <h3 className="text-xl font-display font-bold text-foreground mb-2">
                    <span className="text-primary">##</span> {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-lg">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto hash-bg-dark rounded-3xl px-8 sm:px-16 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            <span className="text-blue-400">#</span> GEO-ready in five minutes.
          </h2>
          <p className="text-base text-blue-200/70 mb-10 max-w-xl mx-auto">
            No engineering-heavy rollout. No content rewrite. Just connect your site and give your team a cleaner path to AI discoverability and chat-ready content.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link href="/pricing/" className="px-8 py-3.5 rounded-xl bg-blue-600 text-white font-semibold text-base hover:bg-blue-500 transition-colors">
              Get started free
            </Link>
            <Link href="/platforms/" className="px-8 py-3.5 rounded-xl bg-transparent border border-white/20 text-white font-semibold text-base hover:border-white/40 hover:bg-white/5 transition-colors">
              See platforms
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
